// @ts-strict-ignore
import get from "lodash/get";
import kebabCase from "lodash/kebabCase";

import uuidv4 from "uuid/v4";

import type {
  ContentfulCryptopediaArticleType,
  ContentfulCryptopediaTopicType,
  ContentfulCryptopediaCategoryType,
  ContentfulCryptopediaStoryType,
  ContentfulCryptopediaAuthorType,
  ContentfulCryptopediaGlossaryEntryType
} from "types/contentful";

import type {
  CryptopediaArticleType,
  CryptopediaTopicType,
  CryptopediaStoryType,
  CryptopediaCategoryType,
  CryptopediaAuthorType,
  CryptopediaGlossaryEntryType
} from "types/cryptopedia";

import CryptopediaAPI from "services/api/cryptopedia";
import { logger } from "observability";
import {
  buildImage,
  getContentfulCryptopediaArticlesFromRichText
} from "utils/contentful";
import type { PostPropType } from "types/blog-type";
import { buildContentfulMetaData } from "./common";
import type { Locale } from "i18n/constants";

export const buildCryptopediaArticle = (
  article: ContentfulCryptopediaArticleType,
  includeRefs = false
): CryptopediaArticleType =>
  article
    ? {
        id: get(article.sys, "id", uuidv4()),
        title: get(article.fields, "title", ""),
        slug: get(article.fields, "slug", ""),
        pageMeta: buildContentfulMetaData(
          get(article.fields, "pageMeta.fields", null),
          `/cryptopedia/${get(article.fields, "slug", "")}`
        ),
        subtitle: get(article.fields, "subtitle", ""),
        summary: get(article.fields, "summary", {}),
        featuredImage: buildImage(get(article.fields, "featuredImage", {})),
        body: get(article.fields, "body", {}),
        relatedArticles: includeRefs
          ? buildCryptopediaArticles(get(article.fields, "relatedPosts", []))
          : [],
        topics: includeRefs
          ? buildCryptopediaTopics(get(article.fields, "topics", []))
          : [],
        authors: includeRefs
          ? buildCryptopediaAuthorList(get(article.fields, "authors", []))
          : [],
        createdAt: get(article.sys, "firstPublishedAt", ""),
        updatedAt: get(article.sys, "updatedAt", null)
      }
    : null;

const buildCryptopediaAuthorList = (
  authors: ContentfulCryptopediaAuthorType[]
) => {
  return authors.map((author) => buildCryptopediaAuthor(author));
};

export const buildCryptopediaArticles = (
  articles: ContentfulCryptopediaArticleType[],
  includeRefs = false
): CryptopediaArticleType[] =>
  articles &&
  articles.map((article) => buildCryptopediaArticle(article, includeRefs));

export const buildCryptopediaStory = (
  story: ContentfulCryptopediaStoryType,
  withArticles = false
): CryptopediaStoryType =>
  story && {
    id: get(story.sys, "id", uuidv4()),
    name: get(story.fields, "name", ""),
    slug: get(story.fields, "slug", ""),
    pageMeta: buildContentfulMetaData(
      get(story.fields, "pageMeta.fields", null),
      `/cryptopedia/stories/${get(story.fields, "slug", "")}`
    ),
    description: get(story.fields, "description", ""),
    body: get(story.fields, "body", ""),
    articles:
      withArticles &&
      buildCryptopediaArticles(
        getContentfulCryptopediaArticlesFromRichText(
          get(story.fields, "body", null)
        )
      )
  };

export const buildCryptopediaStories = (
  stories: ContentfulCryptopediaStoryType[],
  withArticles = false
): CryptopediaStoryType[] =>
  stories && stories.map((story) => buildCryptopediaStory(story, withArticles));

export const buildCryptopediaCategory = (
  category: ContentfulCryptopediaCategoryType,
  topics: ContentfulCryptopediaTopicType[] = null
): CryptopediaCategoryType =>
  category && {
    id: get(category.sys, "id", uuidv4()),
    name: get(category.fields, "name", ""),
    description: get(category.fields, "description", ""),
    featuredImage: get(category.fields, "featuredImage", ""),
    parentCategory: buildCryptopediaCategory(
      get(category.fields, "parentCategory", null)
    ),
    topics: topics && buildCryptopediaTopics(topics)
  };

export const buildCryptopediaCategories = (
  categories: ContentfulCryptopediaCategoryType[]
): CryptopediaCategoryType[] =>
  categories &&
  categories.map((category) => buildCryptopediaCategory(category));

export const buildCryptopediaTopic = (
  topic: ContentfulCryptopediaTopicType,
  includeRefs = false
): CryptopediaTopicType =>
  topic && {
    id: get(topic.sys, "id", uuidv4()),
    name: get(topic.fields, "name", ""),
    slug: get(topic.fields, "slug", ""),
    pageMeta: buildContentfulMetaData(
      get(topic.fields, "pageMeta.fields", null),
      `/cryptopedia/topics/${get(topic.fields, "slug", "")}`
    ),
    description: get(topic.fields, "description", ""),
    categories: buildCryptopediaCategories(
      get(topic.fields, "categories", null)
    ),
    relatedTopics: includeRefs
      ? buildCryptopediaTopics(get(topic.fields, "relatedTopics", null))
      : null
  };

export const buildCryptopediaTopics = (
  topics: ContentfulCryptopediaTopicType[]
): CryptopediaTopicType[] =>
  topics && topics.map((topic) => buildCryptopediaTopic(topic));

export const buildCryptopediaAuthor = (
  author: ContentfulCryptopediaAuthorType,
  articles = []
): CryptopediaAuthorType =>
  author && {
    id: get(author.sys, "id", uuidv4()),
    name: get(author.fields, "name", ""),
    slug: get(author.fields, "slug", ""),
    jobTitle: get(author.fields, "jobTitle", ""),
    bio: get(author.fields, "bio", ""),
    profileImage:
      get(author.fields, "profileImage.fields.file.url", "") + "?fm=webp",
    twitterUrl: get(author.fields, "twitterUrl", ""),
    linkedInUrl: get(author.fields, "linkedInUrl", ""),
    websiteUrl: get(author.fields, "websiteUrl", ""),
    facebookUrl: get(author.fields, "facebookUrl", ""),
    latestArticleUrl: get(author.fields, "latestArticleUrl", ""),
    articles: articles.length ? buildCryptopediaArticles(articles) : [],
    nickName: get(author.fields, "nickName", "")
  };

export const buildCryptopediaAuthors = (
  authors: ContentfulCryptopediaAuthorType[]
): CryptopediaAuthorType[] =>
  authors && authors.map((author) => buildCryptopediaAuthor(author));

export const buildCryptopediaGlossaryEntry = (
  entry: ContentfulCryptopediaGlossaryEntryType,
  includeDefinition = true
): CryptopediaGlossaryEntryType => {
  if (!entry) return;
  const data = {
    id: get(entry.sys, "id", uuidv4()),
    name: get(entry.fields, "name", ""),
    ...(includeDefinition && {
      definition: get(entry.fields, "definition", "")
    })
  };
  return data;
};

export const buildCryptopediaGlossaryEntries = (
  entries: ContentfulCryptopediaGlossaryEntryType[],
  includeDefinition = true
): CryptopediaGlossaryEntryType[] =>
  entries &&
  entries.map((entry) =>
    buildCryptopediaGlossaryEntry(entry, includeDefinition)
  );

/*
  Queries
*/

export const getAllArticles = async () => {
  try {
    const articles = await CryptopediaAPI.fetchArticles();
    return buildCryptopediaArticles(get(articles, "items", []));
  } catch (error) {
    logger.error("Error getting cryptopedia articles", error);
    return [];
  }
};

export const getArticleBySlug = async (
  slug: string,
  locale?: Locale
): Promise<CryptopediaArticleType | null> => {
  try {
    const article = await CryptopediaAPI.fetchArticleWithSlug(slug, locale);
    return buildCryptopediaArticle(get(article, "items", [])[0], true);
  } catch (error) {
    logger.error("Error getting cryptopedia article", error);
    return null;
  }
};

export const getArticleByCoin = async (
  coinId: string,
  limit?: number
): Promise<CryptopediaArticleType[] | null> => {
  try {
    const articles = await CryptopediaAPI.fetchArticlesByCoin(coinId, limit);
    return buildCryptopediaArticles(get(articles, "items", []), true);
  } catch (error) {
    logger.error("Error getting cryptopedia article", error);
    return null;
  }
};

export const getCoinsBySlug = async (
  slug: string
): Promise<CryptopediaArticleType | null> => {
  try {
    const article = await CryptopediaAPI.fetchCoinsBySlug(slug);
    return buildCryptopediaArticle(get(article, "items", [])[0], true);
  } catch (error) {
    logger.error("Error getting cryptopedia article", error);
    return null;
  }
};

export const getCategoryById = async (id: string, locale?: Locale) => {
  try {
    const categories = await CryptopediaAPI.fetchCategoryById(id, locale);
    return buildCryptopediaCategory(categories.items[0]);
  } catch (error) {
    logger.error("Error getting cryptopedia category by id", error);
    return;
  }
};

export const getArticlesByTopic = async (id: string, locale?: Locale) => {
  try {
    const articles = await CryptopediaAPI.fetchArticlesByTopicId(id, locale);
    return buildCryptopediaArticles(articles.items || []);
  } catch (error) {
    logger.error("Error getting cryptopedia articles by topic", error);
    return [];
  }
};

export const filterArticlesByTopic = (
  topicId: string,
  articles: ContentfulCryptopediaArticleType[]
) => {
  return articles.filter((article) => {
    return article.fields.topics?.filter?.((topic) => topicId === topic.sys.id)
      ?.length;
  });
};

export const getSomeStories = async () => {
  try {
    const stories = await CryptopediaAPI.fetchFewStories();
    return buildCryptopediaStories(get(stories, "items", []), true);
  } catch (error) {
    logger.error("Error getting cryptopedia stories", error);
    return [];
  }
};

export const getHomepageStories = async () => {
  try {
    const stories = await CryptopediaAPI.fetchHomepageStories();
    return buildCryptopediaStories(get(stories, "items", []), true);
  } catch (error) {
    logger.error("Error getting cryptopedia stories", error);
    return [];
  }
};

export const getStoryBySlug = async (slug: string) => {
  try {
    const story = await CryptopediaAPI.fetchStoryWithSlug(slug);
    return buildCryptopediaStory(get(story, "items", [])[0], true);
  } catch (error) {
    logger.error("Error getting cryptopedia story", error);
    return null;
  }
};

export const getCategories = async () => {
  try {
    const categories = await CryptopediaAPI.fetchCategories();
    return buildCryptopediaCategories(get(categories, "items", []));
  } catch (error) {
    logger.error("Error getting cryptopedia categories", error);
    return [];
  }
};

export const getParentCategories = async () => {
  const categories = await getCategories();
  return categories.filter((cat) => !cat.parentCategory);
};

export const getCategoriesWithTopics = async () => {
  try {
    const categories = await CryptopediaAPI.fetchCategories();
    const topics = await CryptopediaAPI.fetchTopics();
    const catWithTopics = get(categories, "items", []).map((category) =>
      buildCryptopediaCategory(
        category,
        filterTopicsByCategory(category.sys.id, get(topics, "items", []))
      )
    );
    const parentCategories = catWithTopics.filter((cat) => !cat.parentCategory);
    const subCategories = catWithTopics.filter((cat) => !!cat.parentCategory);
    return parentCategories.map((category) => ({
      ...category,
      subCategories: subCategories.filter((subCategory) => {
        return subCategory.parentCategory.id === category.id;
      })
    }));
  } catch (error) {
    logger.error("Error getting cryptopedia categories with topics", error);
    return [];
  }
};

export const getAllTopics = async () => {
  try {
    const topics = await CryptopediaAPI.fetchTopics();
    return buildCryptopediaTopics(get(topics, "items", []));
  } catch (error) {
    logger.error("Error getting cryptopedia topics", error);
    return [];
  }
};

export const getSomeTopics = async () => {
  try {
    const topics = await CryptopediaAPI.fetchFewTopics();
    return buildCryptopediaTopics(get(topics, "items", []));
  } catch (error) {
    logger.error("Error getting cryptopedia topics", error);
    return [];
  }
};

export const getTopicBySlug = async (slug: string) => {
  try {
    const topic = await CryptopediaAPI.fetchTopicWithSlug(slug);
    return buildCryptopediaTopic(get(topic, "items", [])[0], true);
  } catch (error) {
    logger.error("Error getting cryptopedia topic", error);
    return null;
  }
};

export const filterTopicsByCategory = (
  categoryId: string,
  topics: ContentfulCryptopediaTopicType[]
) => {
  if (!topics) return null;
  return topics.filter((topic) => {
    if (!topic.fields.categories) return null;
    return topic.fields.categories.filter(
      (category) => categoryId === category.sys.id
    ).length;
  });
};

export const getAllAuthors = async () => {
  try {
    const authors = await CryptopediaAPI.fetchAuthors();
    return buildCryptopediaAuthors(get(authors, "items", []));
  } catch (error) {
    logger.error("Error getting cryptopedia authors", error);
    return [];
  }
};

export const getAuthorBySlug = async (slug: string) => {
  try {
    const author = await CryptopediaAPI.fetchAuthorWithSlug(slug);
    const authorEntry = get(author, "items", [])[0];
    const articles = await CryptopediaAPI.fetchArticlesByAuthorId(
      authorEntry.sys.id
    );
    return buildCryptopediaAuthor(authorEntry, get(articles, "items", []));
  } catch (error) {
    logger.error("Error getting cryptopedia author", error);
    return [];
  }
};

export const filterArticlesByAuthor = (
  authorId: string,
  articles: ContentfulCryptopediaArticleType[] = []
) => {
  return articles.filter((article) => {
    return article.fields.authors?.filter?.(
      (author) => authorId === author.sys.id
    )?.length;
  });
};

export const getAllGlossaryEntries = async (includeDefinition = true) => {
  try {
    const entries = await CryptopediaAPI.fetchGlossaryEntries();
    if (entries.total > 1000) {
      const remainingEntries = await CryptopediaAPI.fetchGlossaryEntries(1000);
      entries.items = entries.items.concat(remainingEntries.items);
    }
    return buildCryptopediaGlossaryEntries(
      get(entries, "items", []),
      includeDefinition
    );
  } catch (error) {
    logger.error("Error getting cryptopedia glossary entries", error);
    return [];
  }
};

const buildFormattedGlossary = (items) => {
  const glossary = [
    {
      id: "num",
      title: "#",
      entries: []
    }
  ];
  for (let i = 0; i < 26; i++) {
    const letter = (i + 10).toString(36);
    glossary.push({
      id: letter,
      title: letter.toUpperCase(),
      entries: []
    });
  }
  items.forEach((item) => {
    const name = get(item, "name", "");
    const definition = get(item, "definition", "");
    if (!name || !definition) {
      return;
    }
    const re = RegExp(/[^a-zA-Z]+/);
    const firstLetter = name[0];
    const letter = !re.test(firstLetter) ? firstLetter.toLowerCase() : "num";
    glossary
      .find((x) => x.id === letter)
      .entries.push({
        name: name,
        definition: definition
      });
  });
  return glossary.filter((item) => {
    return item.entries.length;
  });
};

export const getFormattedGlossary = async () => {
  try {
    const articles = await getAllGlossaryEntries();
    return buildFormattedGlossary(articles);
  } catch (error) {
    logger.error("Error getting glossary entries", error);
    return [];
  }
};

export const getCategoryUrl = (categoryName: string) => {
  return `/cryptopedia/explore#${kebabCase(categoryName)}`;
};

export const getGlossaryUrl = (glossaryName: string) => {
  return `/cryptopedia/glossary#${kebabCase(glossaryName)}`;
};

export const getShortenedTextWithEllipsis = (text: string, maxLength) => {
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

export const sanitizeSearchTerm = (term) => {
  return term?.replace(/[^a-zA-Z\s0-9.]/gm, "") ?? "";
};

export const normalizeCryptopediaPosts = (
  posts: CryptopediaArticleType[]
): PostPropType[] => {
  return posts.map((post) => {
    return {
      id: post.id,
      title: post.title,
      slug: post.slug,
      datePublished: post.updatedAt,
      introduction: post.subtitle,
      articleAuthor: post.authors?.[0]?.name,
      postImage: post.featuredImage.url,
      postImageAlt: post.featuredImage.title,
      postSlug: post.slug,
      authorSlug: post.authors?.[0]?.slug
    };
  });
};
